/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable quote-props */
import React, { useState, useEffect, useContext } from "react";
import { Box, Stack, styled, Switch, Typography } from "@mui/material";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { BreakpointContext } from "components/common";
import { BoostMobilePricingTable, BoostPricingTable, ProductTermToggle, TestimonialCards } from "@tomocredit/common-fe/components";
// import { calculatePerMonthPrice, calculatePriceAfterPromotion } from "@tomocredit/common-fe/utils";
import { BOOST_PRICING_DEFAULT_TERM, BOOST_SUBSCRIPTION_FEATURES, boostProductTierConfig_v3, boostTiers, LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import { BoostTerm, BoostTier } from "@tomocredit/common-fe/types";
import GradientButton from "@tomocredit/common-fe/components/common/GradientButton";
import RightArrowIcon from "images/arrow-right-double.svg";
import { CountDownTimer } from "@tomocredit/common-fe/components/common/CountDownTimer";
import { CountDownBanner } from "@tomocredit/common-fe/components/common/CountDownBanner";
import BoostContainer from "./components/BoostContainer";
import CreditImpact from "./components/GoodCreditImpact";

interface IPromoDetails {
  valid: boolean;
  credit?: number;
  type?: "PERCENT_OFF" | "FIXED";
  eligibleProducts?: string[];
}

const boostFeaturesToDisplay = [
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_LINE,
  BOOST_SUBSCRIPTION_FEATURES.NO_HARD_PULL,
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_MONITORING,
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_BOOST,
  BOOST_SUBSCRIPTION_FEATURES.RENT_REPORTING,
  BOOST_SUBSCRIPTION_FEATURES.AI_FINANCIAL_ASSISTANT,
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_REPORT,
];

const IOSSwitch = styled((props: any) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'rgba(170, 146, 249, 1)',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const BoostPricing = () => {
  const { isBelow } = useContext(BreakpointContext);
  const location = useLocation();
  const history = useHistory();
  const belowMd = isBelow("md"); // 768

  const queryParameters = new URLSearchParams(window.location.search);
  const tierRaw = queryParameters.get("tier");
  const termRaw = queryParameters.get("term") || "";
  const emailFromQueryParam = queryParameters.get("email") || "";
  const referralCode = queryParameters.get("referralCode") || "";

  const tierFromQueryParam = (tierRaw || "") in boostTiers ? tierRaw : null;
  const termFromQueryParam = [BoostTerm.YEARLY, BoostTerm.MONTHLY].includes(
    termRaw as BoostTerm,
  )
    ? termRaw
    : null;

  const [checkoutTier, setCheckoutTier] = useState<BoostTier>(
    (tierFromQueryParam || boostTiers.VIP) as BoostTier,
  );
  const [checkoutTerm, setCheckoutTerm] = useState<BoostTerm>(
    (termFromQueryParam || BOOST_PRICING_DEFAULT_TERM) as BoostTerm,
  );
  const [promoDetails, setPromoDetails] = useState<IPromoDetails | undefined>(undefined);

  const tierConfig = boostProductTierConfig_v3[checkoutTerm]?.[checkoutTier];
  // const { name, price, originalPrice } = tierConfig || {};
  const { name } = tierConfig || {};

  // const productKey = `${checkoutTerm?.toLowerCase()}_${checkoutTier.toUpperCase()}`;
  // const isPromoEligible = promoDetails?.valid && (!promoDetails.eligibleProducts?.length || promoDetails.eligibleProducts.includes(productKey));
  // const promotionPercentOff = (isPromoEligible && promoDetails.type === "PERCENT_OFF") ?  promoDetails.credit : 0;
  // const promotionCredit = (isPromoEligible && promoDetails.type === "FIXED") ? (promoDetails.credit || 0) / 100 : 0;

  // const priceAfterPromotion = calculatePriceAfterPromotion(price, promotionPercentOff, promotionCredit);

  // const { perMonthPrice } = calculatePerMonthPrice(
  //   checkoutTerm,
  //   priceAfterPromotion,
  //   originalPrice,
  // );

  const tiers: BoostTier[] = [
    BoostTier.BASIC,
    BoostTier.VIP,
    BoostTier.PREMIUM,
  ];

  const tiersMobile: BoostTier[] = [
    BoostTier.BASIC,
    BoostTier.PREMIUM,
    BoostTier.VIP,
  ];

  const buttonClickCallback = async (tier: BoostTier, term: BoostTerm) => {
    window.gtag?.("event", `pricing_${tier}_${term}_clicked`.toLowerCase(), {
      email: emailFromQueryParam,
      source: LEAD_SOURCES.BOOST_PRICING,
    });
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("term", term);
    urlParams.set("tier", tier);
    if (emailFromQueryParam) {
      urlParams.set("email", emailFromQueryParam);
    }
    if (referralCode) {
      urlParams.set("referralCode", referralCode);
    }
    history.push({ pathname: "/boost-checkout-new", search: urlParams.toString() });
  };

  const fetchPromoCodeDetails = async (code: string) => {
    if (!code) {
      return;
    }
    try {
      const response: { data: IPromoDetails } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/boost/promoCode`, {
        params: { code },
      });
      if (response?.data?.valid) {
        setPromoDetails(response?.data);
      }
    } catch (error) {
      setPromoDetails(undefined);
    }
  };

  // when our banner history pushes from this page
  // we need this useEffect to ensure we update params from history.push
  useEffect(() => {
    const tierRawFromParam = queryParameters.get("tier");
    const termRawFromParam = queryParameters.get("term") || "";
    const tierCleansed = (tierRawFromParam || "") in boostTiers ? tierRaw : null;
    const termCleansed = [BoostTerm.YEARLY, BoostTerm.MONTHLY].includes(
      termRawFromParam as BoostTerm,
    )
      ? termRaw
      : null;

    setCheckoutTier((tierCleansed || boostTiers.VIP) as BoostTier);
    setCheckoutTerm((termCleansed || BOOST_PRICING_DEFAULT_TERM) as BoostTerm);
  }, [location]);

  // Select centered tier if previously-selected tier doesn't exist due to term change
  useEffect(() => {
    if (!tiers.includes(checkoutTier)) {
      setCheckoutTier(tiers[1]!);
    }
  }, [checkoutTerm]);

  useEffect(() => {
    if (referralCode) {
      fetchPromoCodeDetails(referralCode);
    } else {
      setPromoDetails(undefined);
    }
  }, [referralCode]);

  // Mobile view component
  const renderMobileView = () => (
    <Stack
      spacing={3}
      sx={{
        width: "100%",
        marginBottom: "2rem",
      }}
    >
      <Stack
        sx={{
          placeItems: "center",
        }}
      >
        <p className="text-2xl font-normal mb-4">🚀 Boost credit instantly</p>
        <CountDownTimer inputSeconds={480} />
      </Stack>
      <div className="w-full grid place-items-center mt-4">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <p>Monthly</p>
          <IOSSwitch checked={checkoutTerm === BoostTerm.YEARLY} onClick={() => {
            if(checkoutTerm === BoostTerm.YEARLY) {
              setCheckoutTerm(BoostTerm.MONTHLY);
            } else {
              setCheckoutTerm(BoostTerm.YEARLY);
            }
          }} />
          <p className="font-bold">Annual</p>
        </Stack>
      </div>
      <BoostMobilePricingTable
        tiers={tiersMobile}
        checkoutTier={checkoutTier}
        checkoutTerm={checkoutTerm}
        setCheckoutTier={setCheckoutTier}
        promoDetails={promoDetails}
      />
      <Box
        sx={{
          position:"sticky",
          bottom:"2rem",
          width: "100%",
          boxShadow:"0px -4px 12px 0px rgba(0, 0, 0, 0.70), 0px 32px 32px 32px rgba(0, 0, 0, 0.85)",
          borderRadius: 5,
        }}
      >
        <GradientButton
          clickMethod={() => buttonClickCallback(checkoutTier, checkoutTerm)}
        >
          <span className="flex flex-row ignore text-[#241D63] text-lg">
            <p className="mr-2 text-2xl text-center">{name === 'VIP' ? '👑' : ''}</p>
            <p className="grid place-items-center">
              Get {name === "Basic" ? "Starter" : name}
            </p>
            <img alt="" src={RightArrowIcon} />
          </span>
        </GradientButton>
      </Box>
    </Stack>
  );

  // Desktop view component
  const renderDesktopView = () => (
    <>
      <div className="mt-3 md:mt-0 flex flex-col items-center">
        <div className="w-3/4">
          <CountDownBanner inputSeconds={480} />
        </div>
        <div
          className="text-center mb-6"
          style={{ fontSize: 28, width: undefined }}
        >
          Meet the AI Credit Engine that{" "}
          <span className="font-bold text-boost-primary-200">
            Manages & Builds
          </span>{" "}
          Your Credit
        </div>
        <Typography
          sx={{
            fontFamily: 'Gilroy',
            fontSize: "2.25rem",
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: "120%", /* 43.2px */
            letterSpacing: "-0.25px",
            background: "linear-gradient(90deg, #DBC3ED 0%, #B9F 100%)",
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Choose the plan that’s right for you.
        </Typography>
      </div>
      <div className="pb-4">
        <ProductTermToggle
          checkoutTerm={checkoutTerm}
          setCheckoutTerm={setCheckoutTerm}
          belowMd={belowMd}
        />
      </div>
      <BoostPricingTable
        tiers={tiers}
        highlightTier={boostTiers.VIP}
        checkoutTerm={checkoutTerm}
        buttonClickCallback={(tier: BoostTier, term: BoostTerm) => buttonClickCallback(tier, term)}
        isMobileView={isBelow("lg")}
        selectedFeatures={Object.keys(BOOST_SUBSCRIPTION_FEATURES).filter((f) =>
          boostFeaturesToDisplay.includes(f),
        )}
        isSecondaryJoinVisible={false}
        promoDetails={promoDetails}
        theme="black"
        isLanding
      />
      <TestimonialCards />
      <CreditImpact className="mt-38 lg:mt-20 max-w-[1244px]" />
    </>
  );

  return (
    <div className="w-full h-full relative">
      <BoostContainer relative disablePopup background="pricing">
        <div className="mx-auto text-white flex flex-col items-center h-full w-full mdl:py-20 z-50 space-y-10 p-6">
          {belowMd ? renderMobileView() : renderDesktopView()}
        </div>
      </BoostContainer>
    </div>
  );
};

export default BoostPricing;
